// and filters out cSectionOnly / vaginalOnly lessons based on the user's birth_type.

import { useMemo } from "react";
import { useUserProfile } from "./useUserProfile";
import { useBirthTypeAnswer } from "./queries/useBirthTypeAnswer";
import { useLessonsCatalog } from "./queries/useLessonsCatalog";
import { useUserProgress } from "./queries/useUserProgress";
import { createLogger } from "../lib/logger";
import type {
  RecoveryLesson as Lesson,
  RecoveryLessonProgress as LessonProgress,
} from "../types/recovery";

const log = createLogger("useLessons");

export type BirthType = "vaginal" | "csection" | "assisted" | "unknown";

/** Normalise the various spellings stored in Firestore (e.g. "c_section", "c-section" → "csection") */
function normaliseBirthType(raw?: string | null): BirthType {
  if (!raw) return "unknown";
  const v = raw.toLowerCase().trim().replace(/[-_ ]/g, "");
  if (v === "csection" || v === "cesareansection" || v === "caesareansection") {
    return "csection";
  }
  if (v === "vaginal" || v === "vaginalbirth") {
    return "vaginal";
  }
  if (v === "instrumental" || v === "assisted" || v === "forceps" || v === "ventouse") {
    return "assisted";
  }
  return "unknown";
}

/**
 * Return true if this lesson should be visible for the given birth type.
 * If birth type is unknown → show everything (safe default).
 */
function isLessonVisibleForBirthType(lesson: Lesson, birthType: BirthType): boolean {
  if (birthType === "unknown") return true; // show all when we don't know

  if (lesson.cSectionOnly && birthType !== "csection") return false;
  if (lesson.vaginalOnly && birthType === "csection") return false;

  return true;
}

export interface UseLessonsResult {
  /** All enabled lessons grouped by week, filtered by birth type */
  weeks: Record<number, Lesson[]>;
  /** User progress keyed by lesson id */
  progress: Record<string, LessonProgress>;
  /** True while either lessons or progress are loading */
  loading: boolean;
  /** Resolved birth type */
  birthType: BirthType;
}

export function useLessons(): UseLessonsResult {
  const { profile } = useUserProfile();
  const { data: questionnaireBirthType } = useBirthTypeAnswer();
  const { data: lessonsCatalog, isLoading: lessonsLoading } = useLessonsCatalog();
  const { data: progress = {}, isLoading: progressLoading } = useUserProgress();

  const profileBirthType = useMemo(() => {
    if (typeof profile?.birth_type === "string") return profile.birth_type;
    if (typeof profile?.birthType === "string") return profile.birthType;
    return null;
  }, [profile]);

  const birthType = useMemo(
    () => normaliseBirthType(questionnaireBirthType ?? profileBirthType),
    [questionnaireBirthType, profileBirthType],
  );

  // ── Group + filter lessons by birth type (derived from cached catalog) ───
  const weeks = useMemo(() => {
    if (!lessonsCatalog) return {};
    const grouped: Record<number, Lesson[]> = {};
    for (const lesson of lessonsCatalog) {
      if (!isLessonVisibleForBirthType(lesson, birthType)) continue;
      if (!grouped[lesson.week]) grouped[lesson.week] = [];
      grouped[lesson.week].push(lesson);
    }
    log.debug("Lessons filtered by birth type", { birthType, totalWeeks: Object.keys(grouped).length });
    return grouped;
  }, [lessonsCatalog, birthType]);

  return {
    weeks,
    progress,
    loading: lessonsLoading || progressLoading,
    birthType,
  };
}
